import { Alert, Anchor, Button, Container, Stack, Text, Title } from "@mantine/core";
import { useQuery } from "@tanstack/react-query";
import { Link, useLocation } from "@tanstack/react-router";
import { LoadingContent } from "@/components/LoadingContent";
import { getAppInfo, type appInfoSchema } from "./api";
import type { z } from "zod/v4";
export function availabilityNotice(info: z.infer<typeof appInfoSchema>) {
  if (info.maintenanceMode) return { title: "TripBears is under maintenance", message: info.maintenanceMessage.trim() ? info.maintenanceMessage : "We’re doing some maintenance. Please try again soon." };
  if (info.apiDisabled) return { title: "TripBears is temporarily unavailable", message: "The customer API is temporarily disabled. Please try again soon." };
  return null;
}
export function availabilityOptions() {
  return { queryKey: ["app-availability"], queryFn: ({ signal }: { signal: AbortSignal }) => getAppInfo(signal), staleTime: 0, refetchInterval: 30_000, refetchOnWindowFocus: true, retry: false as const };
}
export function AvailabilityBoundary({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  const query = useQuery(availabilityOptions());
  // These routes retain their own session/account/admin guards. General is the
  // only admin page that the disabled API permits for recovery.
  if (location.pathname.startsWith("/auth/") || ["/admin/general", "/legal", "/mobile-apps", "/attributions", "/banned", "/locked"].includes(location.pathname)) return children;
  const notice = query.data ? availabilityNotice(query.data) : null;
  if (query.data && !notice) return <>
    {query.error ? <Alert color="yellow">Availability could not be refreshed. <Button variant="subtle" loading={query.isFetching} onClick={() => void query.refetch()}>Retry</Button></Alert> : null}
    {children}
  </>;
  return <Container size="sm" py="xl"><Stack>
    <Title order={1}>{notice?.title ?? "Checking TripBears availability"}</Title>
    {notice ? <Text style={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}>{notice.message}</Text> : query.isPending ? <LoadingContent label="Checking application availability…" variant="text" /> : null}
    {query.error ? <Alert color="red">Current availability could not be verified. Please retry.</Alert> : null}
    <Button variant="default" loading={query.isFetching} onClick={() => void query.refetch()}>Check again</Button>
    <Anchor renderRoot={props => <Link {...props} to="/auth/sign-in" search={{ next: "/admin/general" }} />}>Administrator sign in</Anchor>
    <Anchor renderRoot={props => <Link {...props} to="/admin/general" />}>Administrator recovery</Anchor>
  </Stack></Container>;
}
